/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { serviceTypes } from '../../consts';
import LoadingSpinner from '../common/LoadingSpinner';
import ExtraSkillsSelect from './FreelancerDetailsSelectExtraSkills';
import { TFreelancerData, TRoleDetails, TServicesStructure, TVehicleDetails } from '../../types';
import { executeHttpRequest } from '../../epics/api';
import { getErrorMessages } from '../../utils';


type TSelectedRole = {
    label: string;
    value: string;
}

type TCurrentRole = {
    label: string;
    value: string;
    vehicle_type?: number;
    type?: string;
}

export type TAddNewRoleRow = {
    fetchListOf: (type: string, url: string) => void;
    freelancerData: TFreelancerData;
    roles:  TRoleDetails[] | TVehicleDetails[];
    roleName: 'vehicle_type' | 'type';
    servicesStructure: TServicesStructure;
    serviceFilterName: string;
    setEditingID: (id: number) => void;
    setError: (error: string) => void;
};

export function AddNewRoleRow({
    fetchListOf,
    freelancerData,
    roles,
    roleName,
    servicesStructure,
    serviceFilterName,
    setEditingID,
    setError,
}: TAddNewRoleRow) {

    const [fields, setFields] = useState<TRoleDetails>();
    const [rolesOptions, setRolesOptions] = useState([]);
    const [currentSelectedRole, setCurrentSelectedRole] = useState({} as TCurrentRole);
    const [isAddingNewRole, setIsAddingNewRole] = useState(false);

    const setOptions = () => {
        const serviceStructure = servicesStructure[freelancerData.service_key];

        if (serviceStructure) {
            const roleFilterName = serviceStructure.role_filter_name;
            const roleDetailObject = serviceStructure?.[roleFilterName];
            const selectedRoleTypes =
                roles.length
                    ? roles.map(role => role?.[roleName])
                    : [];

            const currentRolesOptions = Object.entries(roleDetailObject)
                .filter(([roleType]) => !selectedRoleTypes.map(String).includes(roleType) && roleType !== '')
                .map(([roleType, roleInfo]) => ({
                    // @ts-ignore
                    label: roleInfo.label,
                    value: roleType,
                }));
            const filteredCurrentRolesOptions = currentRolesOptions
                .filter(role => role.label !== 'Motorcycle / Scooter');
            setRolesOptions(filteredCurrentRolesOptions);
          }
    };

    useEffect(() => {
        setOptions();
    }, [roles]);

    const handleCurrentSelectedRole = (selectedRole: TSelectedRole) => {
        if (selectedRole) {
            setCurrentSelectedRole({
                value: selectedRole.value,
                label: selectedRole.label,
                [roleName]: selectedRole.value,
            });
        } else {
            setCurrentSelectedRole({} as TCurrentRole);
        }
    };

    const handleCheckboxFieldChange = (event: { target: HTMLInputElement }, fieldName: string) => {
        setFields({
            ...(fields as TRoleDetails),
            [fieldName]: event.target.checked,
        });
    };

    const handleAddNewRole = () => {
        setIsAddingNewRole(true);
        setError(null);

        executeHttpRequest(
            `freelancers/${freelancerData.id}/${serviceFilterName}/`,
            'post',
            {
                ...(freelancerData.service_key === serviceTypes.DRIVER && { own_vehicle: false }),
                ...fields,
                [roleName]: currentSelectedRole.value,
            },
        )
            .then(() => {
                setEditingID(null);
                fetchListOf('roles', `freelancers/${freelancerData.id}/${serviceFilterName}/`);
                setIsAddingNewRole(false);
                setFields(null);
                setRolesOptions([]);
                setCurrentSelectedRole({} as TCurrentRole);
            })
            .catch(err => {
                setIsAddingNewRole(false);
                setError(getErrorMessages(err));
            });
    };

    return (
        <tr>
            <td>
                <Select
                    value={currentSelectedRole}
                    clearable={false}
                    onChange={handleCurrentSelectedRole}
                    options={rolesOptions}
                    backspaceRemoves={false}
                    placeholder={rolesOptions.length ? 'Select role' : 'No more roles available'}
                    disabled={!rolesOptions.length}
                />
            </td>
            <td>
                <ExtraSkillsSelect
                    serviceKey={freelancerData.service_key}
                    selectedRole={currentSelectedRole}
                    servicesStructure={servicesStructure}
                    disabled={false}
                    fields={fields}
                    update={setFields}
                />
            </td>
            <td data-testid='active'>
                <input
                    name='active'
                    className="checkbox-purple"
                    type="checkbox"
                    checked={!currentSelectedRole.value ? false : null}
                    onChange={event => {
                        handleCheckboxFieldChange(event, 'active');
                    }}
                />
            </td>
            {freelancerData.service_key === serviceTypes.DRIVER && (
                <td data-testid='own_vehicle'>
                    <input
                        name='own_vehicle'
                        className="checkbox-purple"
                        type="checkbox"
                        checked={!currentSelectedRole.value ? false : null}
                        onChange={event => {
                            handleCheckboxFieldChange(event,  'own_vehicle');
                        }}
                    />
                </td>
            )}
            <td>
                <button
                    className="continue btn--round-big"
                    onClick={handleAddNewRole}
                    disabled={isAddingNewRole || !currentSelectedRole.value}
                    type="button"
                    data-typeid="add-new-role-button"
                    aria-label={!currentSelectedRole.value ? 'Please select a role' : 'Add role'}
                >
                    {isAddingNewRole ? (
                        <LoadingSpinner
                            className="spinner"
                            backgroundColor="white"
                        />
                    ) : (
                        'ADD'
                    )}
                </button>
            </td>
        </tr>
    );
}
